import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { ArrowRight, AlertTriangle, MessageSquare, BarChart3, CheckCircle2, Clock, Shield, Users, Layers, Eye } from 'lucide-react';

const SectionTitle = ({ label, title, subtitle }) => (
  <div className="max-w-3xl">
    {label && <span className="text-xs font-bold text-[#0077FF] uppercase tracking-widest mb-3 block">{label}</span>}
    <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 leading-tight">{title}</h2>
    {subtitle && <p className="text-base md:text-lg text-slate-600 leading-relaxed">{subtitle}</p>}
  </div>
);

const RestaurantsQSRPage = () => {
  const { language, t } = useLanguage();
  const getLocalizedPath = (path) => `/${language}${path}`;

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="pt-36 pb-20 bg-slate-50 border-b border-slate-200" data-testid="restaurants-hero">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold text-[#0077FF] uppercase tracking-widest mb-4 block">{t('restaurantsQsr.hero.label')}</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-testid="restaurants-title">
                {t('restaurantsQsr.hero.title')}
              </h1>
              <p className="text-lg md:text-xl text-[#FF7F00] font-medium leading-relaxed mb-4 italic">
                {t('restaurantsQsr.hero.tagline')}
              </p>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-4">
                {t('restaurantsQsr.hero.subtitle')}
              </p>
              <p className="text-base text-slate-600 leading-relaxed mb-2">
                {t('restaurantsQsr.hero.body1')}
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                {t('restaurantsQsr.hero.body2')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link to={getLocalizedPath('/contact')} className="inline-flex items-center gap-2 bg-[#FF7F00] hover:bg-[#E67200] text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-sm hover:shadow-md active:scale-[0.98]" data-testid="restaurants-hero-cta-primary">
                  {t('restaurantsQsr.hero.ctaPrimary')} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to={getLocalizedPath('/contact')} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-testid="restaurants-hero-cta-secondary">
                  {t('restaurantsQsr.hero.ctaSecondary')}
                </Link>
              </div>
            </div>
            <div className="mt-8 lg:mt-0">
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img src="/restaurant-qsr-industry.png" alt="Restaurants & QSR" className="w-full h-80 object-cover" loading="eager" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2 — Core Positioning */}
      <section className="section-padding" data-testid="restaurants-positioning">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <SectionTitle
            label={t('restaurantsQsr.positioning.label')}
            title={t('restaurantsQsr.positioning.title')}
          />
          <div className="max-w-3xl mt-6">
            <p className="text-base text-slate-600 leading-relaxed mb-4">
              {t('restaurantsQsr.positioning.body1')}
            </p>
            <p className="text-base text-slate-600 leading-relaxed">
              {t('restaurantsQsr.positioning.body2')}
            </p>
          </div>
        </div>
      </section>

      {/* Section 3 — Three Core Capabilities */}
      <section className="section-padding bg-slate-50 border-y border-slate-200" data-testid="restaurants-capabilities">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <SectionTitle
            label={t('restaurantsQsr.capabilities.label')}
            title={t('restaurantsQsr.capabilities.title')}
          />
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {/* Capability 1 — Detect */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <AlertTriangle className="w-6 h-6 text-slate-700 mb-4" />
              <h3 className="text-sm font-bold text-slate-900 mb-3">{t('restaurantsQsr.capabilities.detect.title')}</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">{t('restaurantsQsr.capabilities.detect.body')}</p>
              <ul className="space-y-2">
                {(t('restaurantsQsr.capabilities.detect.examples') || []).map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-slate-600 leading-relaxed mt-4">{t('restaurantsQsr.capabilities.detect.footer')}</p>
            </div>

            {/* Capability 2 — Resolve */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <MessageSquare className="w-6 h-6 text-slate-700 mb-4" />
              <h3 className="text-sm font-bold text-slate-900 mb-3">{t('restaurantsQsr.capabilities.resolve.title')}</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">{t('restaurantsQsr.capabilities.resolve.body1')}</p>
              <p className="text-sm text-slate-600 leading-relaxed">{t('restaurantsQsr.capabilities.resolve.body2')}</p>
            </div>

            {/* Capability 3 — Visibility */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <Eye className="w-6 h-6 text-slate-700 mb-4" />
              <h3 className="text-sm font-bold text-slate-900 mb-3">{t('restaurantsQsr.capabilities.visibility.title')}</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">{t('restaurantsQsr.capabilities.visibility.body')}</p>
              <ul className="space-y-2">
                {(t('restaurantsQsr.capabilities.visibility.items') || []).map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 — Operational Workflow */}
      <section className="section-padding" data-testid="restaurants-workflow">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <SectionTitle
            label={t('restaurantsQsr.workflow.label')}
            title={t('restaurantsQsr.workflow.title')}
          />
          <div className="grid md:grid-cols-5 gap-6 mt-12">
            {(t('restaurantsQsr.workflow.steps') || []).map((step, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-6 border border-slate-200 text-center">
                <span className="text-xs font-bold text-[#0077FF] mb-3 block">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="text-sm font-bold text-slate-900">{step}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5 — Multi-Location Operations */}
      <section className="section-padding bg-slate-900 text-white" data-testid="restaurants-multi-location">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="text-xs font-bold text-red-400 uppercase tracking-widest mb-4 block">{t('restaurantsQsr.multiLocation.label')}</span>
            <h2 className="text-2xl md:text-3xl font-bold mb-8 leading-tight">{t('restaurantsQsr.multiLocation.title')}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(t('restaurantsQsr.multiLocation.items') || []).map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-slate-300">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 6 — Deployment */}
      <section className="section-padding" data-testid="restaurants-deployment">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <SectionTitle
            label={t('restaurantsQsr.deployment.label')}
            title={t('restaurantsQsr.deployment.title')}
          />
          <div className="max-w-3xl mt-6">
            <p className="text-base text-slate-600 leading-relaxed mb-4">{t('restaurantsQsr.deployment.body1')}</p>
            <p className="text-base text-slate-600 leading-relaxed mb-8">{t('restaurantsQsr.deployment.body2')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {(t('restaurantsQsr.deployment.steps') || []).map((step, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <Layers className="w-5 h-5 text-slate-700 mb-3" />
                <h3 className="text-sm font-bold text-slate-900">{step}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7 — Accountability */}
      <section className="section-padding bg-slate-50 border-y border-slate-200" data-testid="restaurants-accountability">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <SectionTitle
            label={t('restaurantsQsr.accountability.label')}
            title={t('restaurantsQsr.accountability.title')}
          />
          <div className="max-w-3xl mt-6">
            <p className="text-base text-slate-600 leading-relaxed mb-6">{t('restaurantsQsr.accountability.body')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(t('restaurantsQsr.accountability.items') || []).map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-slate-200">
                <Shield className="w-5 h-5 text-slate-700 mb-3" />
                <h3 className="text-sm font-bold text-slate-900">{item}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-slate-900 text-white" data-testid="restaurants-cta">
        <div className="container-max px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-8">{t('restaurantsQsr.cta.title')}</h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={getLocalizedPath('/contact')} className="inline-flex items-center gap-2 bg-[#FF7F00] hover:bg-[#E67200] text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-sm hover:shadow-md active:scale-[0.98]" data-testid="restaurants-bottom-cta-primary">
              {t('restaurantsQsr.cta.ctaPrimary')} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to={getLocalizedPath('/contact')} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-slate-500 text-sm font-medium text-white hover:bg-slate-800 transition-colors" data-testid="restaurants-bottom-cta-secondary">
              {t('restaurantsQsr.cta.ctaSecondary')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RestaurantsQSRPage;
